import type { QueryClient } from "@tanstack/react-query";
import { beginPrivateSessionChange, getPrivateSessionState } from "@/lib/api/private-session";
import { signOutAdminFirebaseAuth } from "@/lib/firebase/auth-session";
import { resetPrivateState, recoverPrivateBinding, sessionChannel } from "./sessionChannel";

export async function changePrivateSession<T>(client: QueryClient, operation: () => Promise<T>): Promise<T> {
  beginPrivateSessionChange();
  const generation = getPrivateSessionState().generation;
  const assertOwnedChange = () => {
    const current = getPrivateSessionState();
    if (current.generation !== generation || !current.changing) throw new DOMException("Session change superseded", "AbortError");
  };
  const end = sessionChannel.begin();
  try {
    await resetPrivateState(client);
    assertOwnedChange();
    await signOutAdminFirebaseAuth();
    assertOwnedChange();
    const result = await operation();
    assertOwnedChange();
    return result;
  } finally {
    try {
      if (generation === getPrivateSessionState().generation) await recoverPrivateBinding(client, generation);
    } catch { /* Unknown binding stays paused; recovery retries only the session read. */ }
    end();
    if (getPrivateSessionState().changing) sessionChannel.recover();
  }
}
